import { useState, useEffect } from 'react'
import { Plus, Edit2, Trash2, Star, Eye, EyeOff, X } from 'lucide-react'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import { gestaoService } from '../../services/gestaoService'
import toast from 'react-hot-toast'

function ModalForm({ editando, onClose, onSalvar }) {
  const [form, setForm] = useState({
    nome: editando?.nome || '',
    is_padrao: editando?.is_padrao || false,
  })
  const [loading, setLoading] = useState(false)
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }))

  const handleSalvar = async () => {
    if (!form.nome.trim()) return toast.error('Informe o nome')
    setLoading(true)
    await onSalvar(form)
    setLoading(false)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-800">
            {editando ? 'Editar' : 'Nova'} Forma de Pagamento
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Nome *</label>
            <input
              autoFocus value={form.nome} onChange={e => set('nome', e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSalvar()}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              placeholder="Ex: PIX, Cheque, Transferência..."
            />
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="padrao" checked={form.is_padrao} onChange={e => set('is_padrao', e.target.checked)}
              className="w-4 h-4 rounded" />
            <label htmlFor="padrao" className="text-sm text-gray-600">Definir como padrão</label>
          </div>
        </div>
        <div className="flex gap-3 px-6 py-4 border-t border-gray-100">
          <button onClick={onClose} className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
            Cancelar
          </button>
          <button onClick={handleSalvar} disabled={loading}
            className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium disabled:opacity-50">
            {loading ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default function FormasPagamento() {
  const { organizadorAtivo } = useOrganizador()
  const orgId = organizadorAtivo?.id

  const [formas, setFormas] = useState([])
  const [busca, setBusca] = useState('')
  const [modalForm, setModalForm] = useState(null) // null | 'nova' | {forma}
  const [loading, setLoading] = useState(false)

  const carregar = async () => {
    if (!orgId) return
    setLoading(true)
    try {
      const res = await gestaoService.listarFormasPagamento(orgId)
      setFormas(res.data || [])
    } catch {
      toast.error('Erro ao carregar formas de pagamento')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { carregar() }, [orgId])

  const handleSalvar = async (dados) => {
    try {
      if (typeof modalForm === 'object' && modalForm?.id) {
        await gestaoService.atualizarFormaPagamento(orgId, modalForm.id, dados)
        toast.success('Atualizado!')
      } else {
        await gestaoService.criarFormaPagamento(orgId, dados)
        toast.success('Criado!')
      }
      setModalForm(null)
      carregar()
    } catch {
      toast.error('Erro ao salvar')
    }
  }

  const handleToggleAtivo = async (fp) => {
    try {
      await gestaoService.atualizarFormaPagamento(orgId, fp.id, { is_ativo: !fp.is_ativo })
      carregar()
    } catch {
      toast.error('Erro ao atualizar')
    }
  }

  const handleSetPadrao = async (fp) => {
    if (fp.is_padrao) return
    try {
      await gestaoService.atualizarFormaPagamento(orgId, fp.id, { is_padrao: true })
      carregar()
    } catch {
      toast.error('Erro ao definir padrão')
    }
  }

  const [confirmExcluir, setConfirmExcluir] = useState(null)

  const handleExcluir = async (fp) => {
    setConfirmExcluir(fp)
  }

  const confirmarExclusao = async () => {
    const fp = confirmExcluir
    setConfirmExcluir(null)
    try {
      await gestaoService.excluirFormaPagamento(orgId, fp.id)
      toast.success('Excluído!')
      carregar()
    } catch {
      toast.error('Erro ao excluir')
    }
  }

  const formasFiltradas = formas.filter(f =>
    f.nome.toLowerCase().includes(busca.toLowerCase())
  )

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-bold text-gray-800">Formas de Pagamento</h1>

      <div className="bg-white rounded-xl border border-gray-200 p-4 overflow-hidden">
        <div className="flex items-center gap-3 mb-4">
          <input value={busca} onChange={e => setBusca(e.target.value)}
            placeholder="Buscar formas de pagamento..."
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm flex-1 max-w-sm" />
          <button onClick={() => setModalForm('nova')}
            className="ml-auto flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium">
            <Plus className="w-4 h-4" />
            + Nova forma de pagamento
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <table className="w-full">
            <thead className="border-b border-gray-200">
              <tr>
                {['Nome', 'Status', 'Padrão', 'Ações'].map(h => (
                  <th key={h} className="pb-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-left px-2">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {formasFiltradas.length === 0 ? (
                <tr>
                  <td colSpan={4} className="py-10 text-center text-gray-400 text-sm">
                    Nenhuma forma de pagamento cadastrada
                  </td>
                </tr>
              ) : formasFiltradas.map(fp => (
                <tr key={fp.id} className="border-b border-gray-50 hover:bg-gray-50">
                  <td className="px-2 py-3 text-sm text-gray-700 font-medium">{fp.nome}</td>
                  <td className="px-2 py-3">
                    <span className={`inline-block px-2 py-1 rounded-md text-xs font-semibold ${fp.is_ativo ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {fp.is_ativo ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="px-2 py-3">
                    {fp.is_padrao ? (
                      <span className="inline-block px-2 py-1 rounded-md text-xs font-semibold bg-blue-100 text-blue-700">Padrão</span>
                    ) : (
                      <button onClick={() => handleSetPadrao(fp)} title="Definir como padrão"
                        className="text-gray-300 hover:text-yellow-400 transition-colors">
                        <Star className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                  <td className="px-2 py-3">
                    <div className="flex items-center gap-1">
                      <button onClick={() => setModalForm(fp)} title="Editar"
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors">
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleToggleAtivo(fp)} title={fp.is_ativo ? 'Desativar' : 'Ativar'}
                        className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors">
                        {fp.is_ativo ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                      {!fp.is_padrao && (
                        <button onClick={() => handleExcluir(fp)} title="Excluir"
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modalForm && (
        <ModalForm
          editando={typeof modalForm === 'object' ? modalForm : null}
          onClose={() => setModalForm(null)}
          onSalvar={handleSalvar}
        />
      )}

      {/* Modal Confirmação Exclusão */}
      {confirmExcluir && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl">
            <h3 className="font-bold text-gray-900 text-lg mb-2 text-center">Excluir forma de pagamento?</h3>
            <p className="text-gray-600 text-sm text-center mb-5">
              A forma <strong>"{confirmExcluir.nome}"</strong> será excluída permanentemente.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmExcluir(null)} className="flex-1 border border-gray-300 text-gray-700 py-2.5 rounded-lg font-medium text-sm hover:bg-gray-50 transition">
                Cancelar
              </button>
              <button onClick={confirmarExclusao} className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2.5 rounded-lg font-medium text-sm transition">
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
